/**
 * WLSN Cart – Central module for cart state and updates.
 * Defines sections that are updated, API calls and event listeners.
 */
if (!customElements.get('wlsn-cart')) {
  const WLSN_OWN_REQUEST_HEADER = 'X-WLSN-Cart-Request';
  const CART_MUTATION_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /**
   * Patch window.fetch once so cart mutations made outside WLSNCart (e.g. an app like
   * BOGOS calling /cart/update.js to add a free gift after our own add-to-cart request
   * already resolved) still trigger a cart sections refresh. Without this, those
   * changes only become visible after a full page reload.
   * @returns {void}
   */
  function patchFetchForThirdPartyCartUpdates() {
    if (window.__wlsnCartFetchPatched) return;
    window.__wlsnCartFetchPatched = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isCartMutation = CART_MUTATION_URL_RE.test(url);
      const isOwnRequest = Boolean(init?.headers?.[WLSN_OWN_REQUEST_HEADER]);
      const promise = nativeFetch(input, init);

      if (isCartMutation && !isOwnRequest) {
        promise
          .then((response) => {
            if (response.ok) window.WLSNCart?.refreshCartSections?.();
          })
          .catch(() => {});
      }

      return promise;
    };
  }

  patchFetchForThirdPartyCartUpdates();

  class WLSNCart extends HTMLElement {
    constructor() {
      super();

      this.isUpdating = false;

      this.CART_SECTIONS = Object.freeze({
        CART_ICON_BUBBLE: 'wlsn-cart-icon-buble',
        CART_DRAWER: 'wlsn-cart-drawer',
        MAIN_CART: 'wlsn-main-cart',
      });

      this.SECTIONS_UPDATE_CONFIG = Object.freeze([
        { 
          id: this.CART_SECTIONS.CART_ICON_BUBBLE, 
          selector: '#wlsn-cart-icon-bubble' 
        },
        {
          id: this.CART_SECTIONS.CART_DRAWER,
          selector: 'wlsn-cart-drawer',
          innerSelector: '.js-wlsn-cart-drawer-content',
        },
        { 
          id: this.CART_SECTIONS.MAIN_CART,
          selector: 'wlsn-main-cart',
          innerSelector: '.js-wlsn-main-cart-wrapper',
        },
      ]);

      this.bindMethods = {
        handleQuantityChange: this._handleQuantityChange.bind(this),
        handleSubscriptionChange: this._handleSubscriptionChange.bind(this),
        handleItemRemove: this._handleItemRemove.bind(this),
        handleNoteChange: this._handleNoteChange.bind(this),
        handleItemAdded: this._handleItemAdded.bind(this),
        handleUpdateCartLine: this._handleUpdateCartLine.bind(this),
      };
    }

    get elements() {
      return {};
    }

    /**
     * Connect the event listeners
     * @returns {void}
     */
    connectedCallback() {
      this._attachEventListeners();
      if (typeof window !== 'undefined') {
        window.WLSNCart = this;
      }
    }

    /**
     * Disconnect the event listeners
     * @returns {void}
     */
    disconnectedCallback() {
      this._detachEventListeners();
      if (typeof window !== 'undefined' && window.WLSNCart === this) {
        window.WLSNCart = null;
      }
    }

    /**
     * Attach the event listeners
     * @returns {void}
     */
    _attachEventListeners() {
      document.addEventListener('wlsn:cart:quantity:change', this.bindMethods.handleQuantityChange);
      document.addEventListener('wlsn:cart:subscription:change', this.bindMethods.handleSubscriptionChange);
      document.addEventListener('wlsn:cart:item:remove', this.bindMethods.handleItemRemove);
      document.addEventListener('wlsn:cart:note:change', this.bindMethods.handleNoteChange);
      document.addEventListener('wlsn:cart:item:added', this.bindMethods.handleItemAdded);
      document.addEventListener('wlsn:cart:update:cart:line', this.bindMethods.handleUpdateCartLine);
    }

    /**
     * Detach the event listeners
     * @returns {void}
     */
    _detachEventListeners() {
      document.removeEventListener('wlsn:cart:quantity:change', this.bindMethods.handleQuantityChange);
      document.removeEventListener('wlsn:cart:subscription:change', this.bindMethods.handleSubscriptionChange);
      document.removeEventListener('wlsn:cart:item:remove', this.bindMethods.handleItemRemove);
      document.removeEventListener('wlsn:cart:note:change', this.bindMethods.handleNoteChange);
      document.removeEventListener('wlsn:cart:item:added', this.bindMethods.handleItemAdded);
      document.removeEventListener('wlsn:cart:update:cart:line', this.bindMethods.handleUpdateCartLine);
    }

    /**
     * Returns a list of section id-s for fetch (cart add/change).
     * @returns {string[]}
     */
    getSectionIds() {
      return this.SECTIONS_UPDATE_CONFIG.map((s) => s.id);
    }

    /**
     * Updates all cart sections in the DOM based on the cart response (sections).
     * @param {{ sections?: Record<string, string> }} cart - Object with cart.sections
     * @returns {void}
     */
    updateSectionsFromCart(cart) {
      if (!cart || !cart.sections) return;

      this.SECTIONS_UPDATE_CONFIG.forEach((sectionConfig) => {
        const { id, selector, innerSelector } = sectionConfig;
        const sectionHtml = cart.sections[id];
        if (!sectionHtml) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const newOuterEl = doc.querySelector(selector);
        if (!newOuterEl) return;

        const currentElements = document.querySelectorAll(selector);
        currentElements.forEach((currentEl) => {
          if (innerSelector) {
            const newInnerEl = newOuterEl.querySelector(innerSelector);
            const currentInnerEl = currentEl.querySelector(innerSelector);
            if (newInnerEl && currentInnerEl && currentInnerEl.parentElement) {
              currentInnerEl.parentElement.replaceChild(newInnerEl.cloneNode(true), currentInnerEl);
            }
          } else if (currentEl.parentElement) {
            currentEl.parentElement.replaceChild(newOuterEl.cloneNode(true), currentEl);
          }
        });
      });
    }

    /**
     * Apply cart response after add-to-cart (or other source).
     * @param {{ sections?: Record<string, string> }} data - Odgovor od Shopify (npr. cart add)
     * @param {{ openDrawer?: boolean }} [options]
     * @returns {void}
     */
    applyCartUpdate(data, options = {}) {
      if (!data || !data.sections) return;
      this.updateSectionsFromCart(data);
      if (options.openDrawer) {
        // Defer one frame so the freshly-replaced drawer paints in its closed state
        // before `active` is added — this lets the slide-in transition actually run.
        requestAnimationFrame(() => {
          this._dispatch('wlsn:cart:drawer:open', {});
        });
      }
    }

    /**
     * Dispatch custom event on document
     * @param {string} eventName - Event name
     * @param {object} detail - Event detail
     * @returns {void}
     */
    _dispatch(eventName, detail) {
      document.dispatchEvent(new CustomEvent(eventName, { detail }));
    }

    /**
     * Get routes from window
     * @returns {{ cartChangeUrl?: string, cartUpdateUrl?: string, cartUrl?: string, cartAddUrl?: string }}
     */
    _getRoutes() {
      return {
        cartChangeUrl: window.routes?.cart_change_url,
        cartUpdateUrl: window.routes?.cart_update_url,
        cartUrl: window.routes?.cart_url,
        cartAddUrl: window.routes?.cart_add_url,
      };
    }

    /**
     * Build payload for cart change/add requests (sections + sections_url).
     * @returns {{ sections: string[], sections_url: string }}
     */
    getSectionsPayload() {
      return {
        sections: this.getSectionIds(),
        sections_url: window.location.pathname,
      };
    }

    /**
     * Re-fetch and apply the current cart sections. Used after a third-party app
     * mutates the cart outside of WLSNCart (e.g. BOGOS adding a free gift) so our
     * custom drawer/cart markup catches up without needing a page reload.
     * @returns {Promise<void>}
     */
    async refreshCartSections() {
      try {
        const url = `${window.location.pathname}?sections=${this.getSectionIds().join(',')}`;
        const response = await fetch(url, {
          headers: { [WLSN_OWN_REQUEST_HEADER]: '1' },
        });
        if (!response.ok) return;
        const sections = await response.json();
        this.updateSectionsFromCart({ sections });
      } catch (error) {
        console.error('[WLSNCart] Failed to refresh cart sections:', error);
      }
    }

    /**
     * Replace a cart line item with a new variant (remove line, add new item). Used for upgrades.
     * Updates sections and dispatches wlsn:cart:update:success with action 'upgrade'.
     * @param {{ lineKey: string, variantId: string, sellingPlanId?: string|null, quantity?: number }} options
     * @returns {Promise<object>} Cart data from the add response (with sections)
     */
    async _replaceCartLineWithVariant({ lineKey, variantId, sellingPlanId = null, quantity = 1 }) {
      if (!lineKey || !variantId) {
        throw new Error('lineKey and variantId are required');
      }

      const routes = this._getRoutes();
      const sectionsPayload = this.getSectionsPayload();

      const changeRes = await fetch(`${routes.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [WLSN_OWN_REQUEST_HEADER]: '1' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          ...sectionsPayload,
        }),
      });
      if (!changeRes.ok) {
        const err = await changeRes.json().catch(() => ({}));
        throw new Error(err.message || err.description || 'Failed to remove item');
      }

      const addItem = { id: Number(variantId), quantity: Number(quantity) };
      if (sellingPlanId) addItem.selling_plan = String(sellingPlanId);

      const addRes = await fetch(routes.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', [WLSN_OWN_REQUEST_HEADER]: '1' },
        body: JSON.stringify({ items: [addItem], ...sectionsPayload }),
      });
      const addData = await addRes.json();
      if (!addRes.ok) {
        throw new Error(
          addData.message || addData.description || (addData.errors?.[0] || 'Failed to add upgrade')
        );
      }
      return addData;
    }

    /**
     * Update cart quantity via API
     * @param {string} lineKey - Line item key
     * @param {number} quantity - New quantity
     * @returns {Promise<object>}
     */
    async _updateCartQuantity(lineKey, quantity) {
      const routes = this._getRoutes();
      const response = await fetch(`${routes.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [WLSN_OWN_REQUEST_HEADER]: '1' },
        body: JSON.stringify({
          id: lineKey,
          quantity: Number(quantity),
          sections: this.getSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.description || 'Failed to update cart quantity');
      }
      return response.json();
    }

    /**
     * Update cart subscription via API
     * @param {number} lineIndex - Line index
     * @param {string|null} sellingPlanId - Selling plan ID
     * @param {number} [quantity] - Quantity
     * @returns {Promise<object>}
     */
    async _updateCartSubscription(lineIndex, sellingPlanId, quantity) {
      const routes = this._getRoutes();
      const payload = {
        line: lineIndex,
        selling_plan: sellingPlanId ? parseInt(sellingPlanId, 10) : null,
        sections: this.getSectionIds(),
        sections_url: window.location.pathname,
      };
      const response = await fetch(`${routes.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [WLSN_OWN_REQUEST_HEADER]: '1' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update subscription: ${response.status}`);
      }
      let cart = await response.json();
      if (quantity != null && quantity !== 1) {
        const qResponse = await fetch(`${routes.cartChangeUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', [WLSN_OWN_REQUEST_HEADER]: '1' },
          body: JSON.stringify({
            line: lineIndex,
            quantity: Number(quantity),
            sections: this.getSectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        if (qResponse.ok) cart = await qResponse.json();
      }
      return cart;
    }

    /**
     * Remove cart item via API
     * @param {string} lineKey - Line item key
     * @returns {Promise<object>}
     */
    async _removeCartItem(lineKey) {
      const routes = this._getRoutes();
      const response = await fetch(`${routes.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [WLSN_OWN_REQUEST_HEADER]: '1' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: this.getSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.description || 'Failed to remove item');
      }
      return response.json();
    }

    /**
     * Update cart note via API
     * @param {string} note - Cart note
     * @returns {Promise<object>}
     */
    async _updateCartNote(note) {
      const routes = this._getRoutes();
      const response = await fetch(`${routes.cartUpdateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [WLSN_OWN_REQUEST_HEADER]: '1' },
        body: JSON.stringify({ note }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.description || 'Failed to update cart note');
      }
      return response.json();
    }

    _handleUpdateCartLine(event) {
      const { lineKey, variantId, sellingPlanId, quantity } = event.detail || {};
      if (this.isUpdating || lineKey == null) return;

      this.isUpdating = true;
      this._dispatch('wlsn:cart:update:start', { lineKey, action: 'upgrade' });

      this._replaceCartLineWithVariant({ lineKey, variantId, sellingPlanId, quantity })
        .then((cart) => {
        this.updateSectionsFromCart(cart);
        this._dispatch('wlsn:cart:update:success', { lineKey, updatedItem: null, cart, action: 'upgrade' });
      })
      .catch((err) => {
        console.error('Failed to upgrade cart line:', err);
        this._dispatch('wlsn:cart:update:error', { lineKey, error: err.message });
      })
      .finally(() => {
        this.isUpdating = false;
      });
    }

    /**
     * Handle quantity change event
     * @param {CustomEvent} event - Event with detail.lineKey, detail.quantity
     * @returns {void}
     */
    _handleQuantityChange(event) {
      const { lineKey, quantity } = event.detail || {};
      if (this.isUpdating || lineKey == null) return;

      this.isUpdating = true;
      this._dispatch('wlsn:cart:update:start', { lineKey, action: 'quantity' });

      this._updateCartQuantity(lineKey, quantity)
        .then((cart) => {
          this.updateSectionsFromCart(cart);
          const updatedItem = cart.items?.find((item) => item.key === lineKey);
          this._dispatch('wlsn:cart:update:success', { lineKey, updatedItem, cart, action: 'quantity' });
        })
        .catch((err) => {
          console.error('Failed to update quantity:', err);
          this._dispatch('wlsn:cart:update:error', { lineKey, error: err.message });
        })
        .finally(() => {
          this.isUpdating = false;
        });
    }

    /**
     * Handle subscription change event
     * @param {CustomEvent} event - Event with detail.lineKey, detail.lineIndex, detail.sellingPlanId, detail.quantity
     * @returns {void}
     */
    _handleSubscriptionChange(event) {
      const { lineKey, lineIndex, sellingPlanId, quantity } = event.detail || {};
      if (this.isUpdating || lineIndex == null) return;

      this.isUpdating = true;
      this._dispatch('wlsn:cart:update:start', { lineKey, action: 'subscription' });

      this._updateCartSubscription(lineIndex, sellingPlanId || null, quantity)
        .then((cart) => {
          this.updateSectionsFromCart(cart);
          const updatedItem = lineKey && cart.items ? cart.items.find((item) => item.key === lineKey) : undefined;
          this._dispatch('wlsn:cart:update:success', { lineKey, updatedItem, cart, action: 'subscription' });
        })
        .catch((err) => {
          console.error('Failed to update subscription:', err);
          this._dispatch('wlsn:cart:update:error', { lineKey, error: err.message });
        })
        .finally(() => {
          this.isUpdating = false;
        });
    }

    /**
     * Handle item remove event
     * @param {CustomEvent} event - Event with detail.lineKey
     * @returns {void}
     */
    _handleItemRemove(event) {
      const { lineKey } = event.detail || {};
      if (this.isUpdating || !lineKey) return;

      this.isUpdating = true;
      this._dispatch('wlsn:cart:update:start', { lineKey, action: 'remove' });

      this._removeCartItem(lineKey)
        .then((cart) => {
          this.updateSectionsFromCart(cart);
          this._dispatch('wlsn:cart:update:success', { lineKey, updatedItem: null, cart, action: 'remove' });
        })
        .catch((err) => {
          console.error('Failed to remove item:', err);
          this._dispatch('wlsn:cart:update:error', { lineKey, error: err.message });
        })
        .finally(() => {
          this.isUpdating = false;
        });
    }

    /**
     * Handle note change event
     * @param {CustomEvent} event - Event with detail.note
     * @returns {void}
     */
    _handleNoteChange(event) {
      const { note } = event.detail || {};
      if (this.isUpdating) return;

      this.isUpdating = true;
      this._updateCartNote(note)
        .then((cart) => {
          this._dispatch('wlsn:cart:note:update:success', { note, cart });
        })
        .catch((err) => {
          console.error('Failed to update cart note:', err);
          this._dispatch('wlsn:cart:note:update:error', { error: err.message });
        })
        .finally(() => {
          this.isUpdating = false;
        });
    }

    /**
     * Handle item added event (e.g. from product form)
     * @param {CustomEvent} event - Event with detail.cart or detail.sections
     * @returns {void}
     */
    _handleItemAdded(event) {
      const { cart: cartData, sections } = event.detail || {};
      const data = cartData && typeof cartData === 'object' ? cartData : { sections };
      if (data.sections) {
        this.updateSectionsFromCart(data);
        this._dispatch('wlsn:cart:drawer:open', {});
      }
    }
  }

  customElements.define('wlsn-cart', WLSNCart);
}
